import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { ContentWithPanelTemplate } from '@experiences/templates/content-with-panel-template';
import { ProspectActivityNotePanelContent } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-activity-note-panel-content';
import { ProspectDetailContent } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-detail-content';
import { ProspectEditPanelContent } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/containers/prospect-edit-panel/prospect-edit-panel-content';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.ProspectDetail
>;

enum RightPanelAction {
  ADD_ACTIVITY_NOTE,
  EDIT_PROSPECT,
  NONE,
}

export const ProspectDetailScreen: React.FC<Props> = ({ route }) => {
  const { restaurantUUID, supplierUUID } = route.params;
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();
  const bottomNavScrollHandler = useBottomNavScrollHandler();

  const [rightPanelAction, setRightPanelAction] = useState<RightPanelAction>(
    RightPanelAction.NONE,
  );
  const [activityNotePanelKey, setActivityNotePanelKey] = useState(0);

  const handleOpenActivityNotePanel = () => {
    setActivityNotePanelKey((key) => key + 1);
    setRightPanelAction(RightPanelAction.ADD_ACTIVITY_NOTE);
  };

  const handlePanelClose = () => {
    setRightPanelAction(RightPanelAction.NONE);
  };

  const renderPanelContent = () => {
    switch (rightPanelAction) {
      case RightPanelAction.EDIT_PROSPECT:
        return (
          <ProspectEditPanelContent
            onClose={handlePanelClose}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        );
      case RightPanelAction.ADD_ACTIVITY_NOTE:
        return (
          <ProspectActivityNotePanelContent
            key={activityNotePanelKey}
            onClose={handlePanelClose}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        );
      case RightPanelAction.NONE:
      default:
        return undefined;
    }
  };

  return (
    <SalesHubShellTemplate>
      <ContentWithPanelTemplate
        onClose={handlePanelClose}
        panelContent={renderPanelContent()}
      >
        <AppPageLayout
          bodyGutter="$24"
          mode="focused"
          onBodyScroll={
            isMobileBottomNavLayout ? bottomNavScrollHandler : undefined
          }
          scroll={false}
          title={i18n.t('sales_portal.prospect_detail.title')}
        >
          <ProspectDetailContent
            onAddActivityNote={handleOpenActivityNotePanel}
            onEditProspect={() =>
              setRightPanelAction(RightPanelAction.EDIT_PROSPECT)
            }
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        </AppPageLayout>
      </ContentWithPanelTemplate>
    </SalesHubShellTemplate>
  );
};
